import { ModelVersionSummary as DangerousModelVersionSummary } from '../../generated-service-lib/ModelService';
import * as generatedServices from '../generatedServices';
import { assertSuccessfulResponse, getValidResponseDataOrThrow, parseISODateStringToDate } from '../helpers';
import { Model, ModelParameter, ModelSummary, ModelVersionSummary } from '../types';

const getSafeModel = (model: { [key: string]: any }) => {
  const name = model?.name || '';
  const calculations = model?.calculations || [];
  const safeCalculations = calculations?.map((c: any) => ({
    ...c,
    outputName: c?.outputName || '',
    function: c?.function || '',
    parameters:
      c?.parameters?.map((p: any) => ({
        ...p,
        name: p?.name || '',
        type: p?.type || '',
        isRequired: p?.isRequired,
      })) || [],
    outputType: c?.outputType || '',
    includeInResults: c?.includeInResults,
    isDisabled: c?.isDisabled,
  }));

  const modelInputs = model?.modelInputs || [];
  const safeModelInputs = modelInputs?.map((input: any) => ({
    ...input,
    name: input?.name || '',
    type: input?.type || '',
    isRequired: input?.isRequired,
  }));

  return {
    name: name || '',
    calculations: safeCalculations || [],
    modelInputs: safeModelInputs || [],
    debugMode: model.debugMode || false,
    isActive: model.isActive || false,
    modelId: model.modelId,
    versionNumber: typeof model.version !== 'undefined' ? model.version : model.versionNumber,
    readOnly: model.readOnly || false,
    createDate: model?.createDate || '',
    lastUpdated: model?.lastUpdated || '',
  };
};

const getSafeModelVersion = (scaryVersion: DangerousModelVersionSummary): ModelVersionSummary => {
  let createDate: Date | undefined = undefined;
  let lastUpdated: Date | undefined = undefined;

  if (typeof scaryVersion.createDate === 'string') {
    createDate = parseISODateStringToDate(scaryVersion.createDate);
  }

  if (typeof scaryVersion.lastUpdated === 'string') {
    lastUpdated = parseISODateStringToDate(scaryVersion.lastUpdated);
  }

  return {
    id: scaryVersion.id || '',
    modelId: scaryVersion.modelId || '',
    versionNumber: scaryVersion.versionNumber,
    description: scaryVersion.description || '',
    isActive: scaryVersion.isActive || false,
    createDate: createDate,
    lastUpdated: lastUpdated,
  };
};

export class ModelService {
  readonly unvalidatedClient: generatedServices.GeneratedModelService = new generatedServices.GeneratedModelService();
  constructor(domain?: string) {
    this.unvalidatedClient = new generatedServices.GeneratedModelService(domain);
  }

  getModelList = async (clientId: string): Promise<ModelSummary[]> => {
    const response = await this.unvalidatedClient.getModels({ clientId });

    if (response.status !== 200) {
      throw new Error('Failed request to Model Service.');
    }

    const modelSummaries = getValidResponseDataOrThrow(response);

    const safeModelSummaries: ModelSummary[] = modelSummaries.map((m: any) => ({
      id: m.id || '',
      versions: (m.versions || []).map(getSafeModelVersion) || [],
    }));

    return safeModelSummaries;
  };

  deleteModelVersion = async ({
    clientId,
    id,
    versionNumber,
  }: {
    clientId: string;
    id: string;
    versionNumber: number;
  }): Promise<void> => {
    const response = await this.unvalidatedClient.deleteModelsByIdVersionsByVersion({
      clientId,
      id,
      version: versionNumber,
    });
    assertSuccessfulResponse(response);
  };

  getModelVersion = async ({
    clientId,
    id,
    versionNumber,
  }: {
    clientId: string;
    id: string;
    versionNumber: number;
  }): Promise<Model> => {
    const response = await this.unvalidatedClient.getModelsByIdVersionsByVersion({
      clientId,
      id,
      version: versionNumber,
    });
    const model = getValidResponseDataOrThrow(response);
    return getSafeModel(model);
  };

  postModelVersion = async ({
    clientId,
    id,
    versionNumber,
    modelVersion,
  }: {
    clientId: string;
    id: string;
    versionNumber: number;
    modelVersion: Model;
  }): Promise<Model> => {
    const response = await this.unvalidatedClient.postModelsByIdVersionsByVersion({
      clientId,
      id,
      version: versionNumber,
      body: modelVersion,
    });
    const model = getValidResponseDataOrThrow(response);
    return getSafeModel(model);
  };

  putModelVersion = async ({
    clientId,
    modelId,
    versionNumber,
    modelVersion,
  }: {
    clientId: string;
    modelId: string;
    versionNumber: number;
    modelVersion: Model;
  }): Promise<Model> => {
    const response = await this.unvalidatedClient.putModelsByIdVersionsByVersion({
      clientId,
      id: modelId,
      version: versionNumber,
      body: modelVersion,
    });
    const model = getValidResponseDataOrThrow(response);
    return getSafeModel(model);
  };

  private putModelVersionIsActive = async ({
    clientId,
    modelId,
    versionNumber,
    isActive,
  }: {
    clientId: string;
    modelId: string;
    versionNumber: number;
    isActive: boolean;
  }): Promise<Model> => {
    const response = await this.unvalidatedClient.putModelsByIdVersionsByVersionByActivate({
      clientId,
      id: modelId,
      version: versionNumber,
      activate: isActive,
    });
    const model = getValidResponseDataOrThrow(response);
    return getSafeModel(model);
  };

  activateModelVersion = async ({
    clientId,
    modelId,
    versionNumber,
  }: {
    clientId: string;
    modelId: string;
    versionNumber: number;
  }): Promise<Model> => {
    return await this.putModelVersionIsActive({ clientId, modelId, versionNumber, isActive: true });
  };

  deactivateModelVersion = async ({
    clientId,
    modelId,
    versionNumber,
  }: {
    clientId: string;
    modelId: string;
    versionNumber: number;
  }): Promise<Model> => {
    return await this.putModelVersionIsActive({ clientId, modelId, versionNumber, isActive: false });
  };

  getModel = async ({ clientId, id }: { clientId: string; id: string }): Promise<Model> => {
    const response = await this.unvalidatedClient.getModelsById({ clientId, id });
    const model = getValidResponseDataOrThrow(response);
    return getSafeModel(model);
  };
  createModel = async (parameters: { clientId: string; id: string; model: Model }) => {
    return await this.unvalidatedClient.postModelsById({
      ...parameters,
      body: parameters.model,
    });
  };
  updateModel = async (parameters: { clientId: string; modelId: string; model: Model }): Promise<Model> => {
    const response = await this.unvalidatedClient.putModelsById({
      clientId: parameters.clientId,
      id: parameters.modelId,
      body: getSafeModel(parameters.model),
    });
    const model = getValidResponseDataOrThrow(response);
    return getSafeModel(model);
  };
  updateModelByVersionNumber = async (parameters: {
    clientId: string;
    modelId: string;
    modelVersion: number;
    model: Model;
  }): Promise<Model> => {
    const response = await this.unvalidatedClient.putModelsByIdVersionsByVersion({
      clientId: parameters.clientId,
      id: parameters.modelId,
      body: getSafeModel(parameters.model),
      version: parameters.modelVersion,
    });
    const model = getValidResponseDataOrThrow(response);
    return getSafeModel(model);
  };
  updateModelInputs = async (parameters: {
    clientId: string;
    id: string;
    inputs: ModelParameter[];
  }): Promise<Model> => {
    const getResponse = await this.unvalidatedClient.getModelsById(parameters);
    const model = getValidResponseDataOrThrow(getResponse);

    if (typeof model !== 'object') {
      throw new Error('Failed to update model');
    }

    model.modelInputs = parameters.inputs;
    const responseModel = await this.updateModel({
      clientId: parameters.clientId,
      modelId: parameters.id,
      model: getSafeModel(model),
    });

    return responseModel;
  };
  deleteModel = async ({ clientId, modelId }: { clientId: string; modelId: string }) => {
    const response = await this.unvalidatedClient.deleteModelsById({ clientId, id: modelId });
    if (response.status !== 200) throw Error('Request failed.');
  };
}
